import { useEffect, useMemo, useState } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

//components
import { SearchBox } from "../components/commons/search.box";

//stores
import { listActivePartners } from "../stores/active.partners";

const PAGE_SIZE = 10;

const CompetencyBadge = ({ competency, color = "green" }) => {
  if (!competency || competency.not_applicable) {
    return (
      <span className="inline-flex rounded-md bg-pink-50 px-2 py-1 text-xs font-medium text-pink-700">
        Not certified
      </span>
    );
  }

  const colorClasses =
    color === "blue"
      ? "bg-blue-50 text-blue-700"
      : "bg-green-50 text-green-700";

  return (
    <span
      className={`inline-flex rounded-md px-2 py-1 text-xs font-medium ${colorClasses}`}
    >
      {competency.highest_level || "Certified"}
    </span>
  );
};

export const TablePartnersList = () => {
  const dispatch = useDispatch();

  const {
    lists = [],
    loading = false,
  } = useSelector((state) => state.activePartners);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(listActivePartners())
      .unwrap()
      .catch((error) => {
        console.error("tables.partners.list", error);
        toast.error("Unable to load partners");
      });
  }, [dispatch]);

  const filteredPartners = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) {
      return lists;
    }

    return lists.filter((partner) => {
      const email = partner.individual?.email || "";

      return email
        .toLowerCase()
        .includes(keyword);
    });
  }, [lists, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPartners.length / PAGE_SIZE),
  );

  const startIndex = (currentPage - 1) * PAGE_SIZE;

  const displayedPartners = filteredPartners.slice(
    startIndex,
    startIndex + PAGE_SIZE,
  );

  return (
    <div className="mt-8">
      <div className="mb-4">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search by email..."
        />
      </div>

      <div className="overflow-x-auto rounded-md bg-white shadow-md">
        <div className="flex items-center justify-between bg-slate-100 px-4 py-4">
          <p className="text-sm text-slate-600">
            Showing {displayedPartners.length} of{" "}
            {filteredPartners.length} partners
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() =>
                setCurrentPage((page) => Math.max(1, page - 1))
              }
              className="rounded px-3 py-1 text-sm disabled:text-gray-300"
            >
              &lsaquo;
            </button>

            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-500 text-sm text-white">
              {currentPage}
            </span>

            <button
              type="button"
              disabled={currentPage === totalPages}
              onClick={() =>
                setCurrentPage((page) =>
                  Math.min(totalPages, page + 1),
                )
              }
              className="rounded px-3 py-1 text-sm disabled:text-gray-300"
            >
              &rsaquo;
            </button>
          </div>
        </div>

        <table className="min-w-full">
          <thead className="border-y border-slate-200 bg-slate-100">
            <tr>
              <th className="px-4 py-4 text-left text-sm">
                Partner
              </th>
              <th className="px-4 py-4 text-left text-sm">
                Contact Information
              </th>
              <th className="px-4 py-4 text-left text-sm">
                Region
              </th>
              <th className="px-4 py-4 text-left text-sm">
                Electrical Wiring
              </th>
              <th className="px-4 py-4 text-left text-sm">
                Electrical Chargeman
              </th>
              <th className="px-4 py-4 text-left text-sm">
                Date Joined
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-200">
            {displayedPartners.map((partner) => (
              <tr key={partner.account_id}>
                <td className="px-4 py-4">
                  <Link
                    to={`/partners/details/${partner.account_id}/registration`}
                    className="text-sm font-bold text-blue-600 underline"
                  >
                    {partner.individual?.name || "Unknown"}
                  </Link>
                </td>

                <td className="px-4 py-4 text-sm text-slate-600">
                  <p>{partner.individual?.email || "-"}</p>
                  <p>{partner.individual?.phone_number || "-"}</p>
                </td>

                <td className="px-4 py-4 text-sm text-slate-600">
                  {partner.application_location?.municipality || "-"},{" "}
                  {partner.application_location?.state || "-"}
                </td>

                <td className="px-4 py-4">
                  <CompetencyBadge
                    competency={
                      partner.electrical_competency?.electrical_wiring
                    }
                  />
                </td>

                <td className="px-4 py-4">
                  <CompetencyBadge
                    competency={
                      partner.electrical_competency?.chargeman
                    }
                    color="blue"
                  />
                </td>

                <td className="px-4 py-4 text-sm text-slate-600">
                  {partner.modified
                    ? new Date(partner.modified).toLocaleDateString()
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!loading && displayedPartners.length === 0 && (
          <div className="p-8 text-center">
            <p className="font-semibold text-gray-500">
              No partners found
            </p>
            <p className="mt-1 text-sm text-gray-400">
              Try a different search.
            </p>
          </div>
        )}

        {loading && (
          <div className="p-8 text-center text-sm text-gray-500">
            Loading partners...
          </div>
        )}
      </div>
    </div>
  );
};
